import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import { Image, ImageIcon } from '@buffer-mono/popcorn'

import { useArticleThumbnail } from '~publish/hooks/resources/useArticleThumbnail'

import type { FeedItem } from '../../hooks/useFeedItems'

import styles from './FeedArticleList.module.css'

const ensureHttps = (url?: string): string | undefined => {
  if (!url) return undefined
  try {
    const urlObject = new URL(url)
    urlObject.protocol = 'https:'
    return urlObject.toString()
  } catch {
    // If URL parsing fails, try a simple replacement
    return url.replace(/^http:/, 'https:')
  }
}

type FeedArticleImageThumbnailProps = {
  title: FeedItem['title']
  articleUrl: FeedItem['articleUrl']
  className?: string
} & React.AnchorHTMLAttributes<HTMLAnchorElement>

export const FeedArticleImageThumbnail = (
  props: FeedArticleImageThumbnailProps,
): JSX.Element => {
  const { title, articleUrl, className, ...rest } = props
  const [imageLoadState, setImageLoadState] = useState<
    'loading' | 'success' | 'error'
  >('loading')

  const { imageUrl } = useArticleThumbnail(articleUrl)
  const firstImageUrl = ensureHttps(imageUrl)

  const loadingState = firstImageUrl ? imageLoadState : 'error'

  return (
    <a
      {...rest}
      className={clsx(className, styles.articleThumbnailContainer)}
      aria-label={`View article: ${title}`}
    >
      <div
        className={clsx(
          styles.articleThumbnailPlaceholder,
          styles[loadingState],
        )}
        aria-hidden="true"
      >
        <ImageIcon size="large" />
      </div>
      {firstImageUrl && (
        <Image
          className={clsx(styles.articleThumbnail, styles[loadingState])}
          onError={(): void => {
            setImageLoadState('error')
          }}
          onLoad={(): void => {
            setImageLoadState('success')
          }}
          src={firstImageUrl}
          alt=""
          role="presentation"
        />
      )}
    </a>
  )
}
